import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const usePasswordRecovery = (setCooldownWithStorage: (seconds: number) => void) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const extractTimeFromError = (error: any): number => {
    try {
      if (typeof error.message === 'string') {
        const match = error.message.match(/after (\d+) seconds/);
        if (match && match[1]) {
          return parseInt(match[1], 10);
        }
      }
      return 60;
    } catch (e) {
      console.error('Error parsing cooldown time:', e);
      return 60;
    }
  };

  const handlePasswordRecovery = async (email: string, cooldown: number) => {
    if (cooldown > 0) {
      toast({
        variant: "destructive",
        title: "Espera un momento",
        description: `Podrás intentarlo nuevamente en ${cooldown} segundos.`,
      });
      return;
    }

    setIsLoading(true);

    try {
      console.log("Initiating custom password recovery for:", email);
      
      // Guardamos el email para usarlo después con el código de recuperación
      localStorage.setItem('recovery_email', email);
      
      // Llamar a nuestra Edge Function personalizada
      const { data, error } = await supabase.functions.invoke('send-password-reset', {
        body: { email }
      });

      if (error) {
        console.error("Edge function error:", error);
        throw new Error(error.message || "Error al enviar el correo de recuperación");
      }

      if (!data?.success) {
        throw new Error("Error al enviar el correo de recuperación");
      }

      console.log("Custom password reset email sent:", data);

      toast({
        title: "Correo enviado",
        description: "Revisa tu bandeja de entrada para continuar con la recuperación. También puedes usar el código que recibirás.",
      });
      
      setCooldownWithStorage(60);
    } catch (error: any) {
      console.error("Password recovery error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "No se pudo enviar el correo de recuperación.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, handlePasswordRecovery };
};